import * as React from 'react';

import {
  enumSalesOrderStatus,
  enumUnit,
  enumWeightDisplayUnit,
  Unit,
  WeightDisplayUnit,
} from '@rematter/frontend-lib-api-client';
import { Button } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { $inventoryAvailabilityVisible } from '../../_state/inventoryAvailability.state';
import {
  $currentDraftLineItemsById,
  $effectiveLineItemsById,
  $hasDraftChangesById,
  $salesOrderDetailById,
  $updateSalesOrder,
} from '../../_state/salesOrderDetail.state';
import { $salesOrderIsMissingRequired } from '../../_state/salesOrderForm.state';
import { useWeightUnitManager } from '../../hooks/useWeightUnitManager';
import { SalesDetailLayout } from '../_layouts/SalesDetailLayout';
import { SalesOrderMaterialsTable } from '../_shared/LineItems';
import { WeightUnitSwitch } from '../_shared/WeightUnitSwitch';

import { convertToLoadFormLineItem } from './helpers/convertSalesOrderToLoadLineItem';
import { validateLineItems } from './helpers/validateLineItems';
import { useSalesOrderMaterialsErrors } from './hooks/useSalesOrderMaterialsErrors';
import { InventoryAvailability } from './InventoryAvailability';
import { InventoryAvailabilitySwitch } from './InventoryAvailabilitySwitch';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import {
  enumLoadWeightType,
  LoadFormLineItem,
} from '~/modules/trade/types/load';
import { SalesOrderLineItemRecord } from '~/modules/trade/types/salesOrder';
import {
  calculateTotalPrice,
  toMoneyInput,
  zeroMoney,
} from '~/modules/trade/utils/money';

type Props = {
  salesOrderId: string;
  disabled?: boolean;
  variant?: 'default' | 'embedded';
};

export const SalesOrderTabMaterials: React.FC<Props> = ({
  salesOrderId,
  disabled = false,
  variant = 'default',
}) => {
  const t = useTranslations();

  // States
  const [lineItemErrors, setLineItemErrors] = React.useState<
    Record<string, string>
  >({});
  const [isSaving, setIsSaving] = React.useState(false);
  const [localWeightDisplayUnit, setLocalWeightDisplayUnit] =
    React.useState<WeightDisplayUnit | null>(null);

  // Atoms
  const salesOrderData = useAtomValue($salesOrderDetailById(salesOrderId));
  const salesOrder = salesOrderData?.data;
  const isMissingRequired = useAtomValue($salesOrderIsMissingRequired);
  const effectiveLineItems = useAtomValue(
    $effectiveLineItemsById(salesOrderId),
  );
  const hasDraftChanges = useAtomValue($hasDraftChangesById(salesOrderId));
  const showInventoryAvailability = useAtomValue($inventoryAvailabilityVisible);
  const setDraftLineItems = useSetAtom(
    $currentDraftLineItemsById(salesOrderId),
  );
  const updateSalesOrder = useSetAtom($updateSalesOrder);

  // Other data hooks
  const toast = useToast();
  const materialsErrors = useSalesOrderMaterialsErrors(lineItemErrors);

  // Derived values
  const isDraftStatus = salesOrder?.status === enumSalesOrderStatus.Draft;

  // Memoized values
  const hasWeightDisplayUnitChanges = React.useMemo(
    () =>
      salesOrder?.weightDisplayUnit &&
      localWeightDisplayUnit !==
        (salesOrder?.weightDisplayUnit ?? enumWeightDisplayUnit.ScaleUnit),
    [localWeightDisplayUnit, salesOrder?.weightDisplayUnit],
  );

  const loadFormLineItems = React.useMemo(
    () => effectiveLineItems.map(convertToLoadFormLineItem),
    [effectiveLineItems],
  );

  // Callbacks
  const handleWeightDisplayUnitChange = React.useCallback(
    (unit: WeightDisplayUnit) => {
      setLocalWeightDisplayUnit(unit);
    },
    [],
  );

  const { currentWeightUnit, handleWeightUnitChange } = useWeightUnitManager({
    weightDisplayUnitPreference: localWeightDisplayUnit,
    onWeightDisplayUnitChange: handleWeightDisplayUnitChange,
    defaultUnit: 'PriceUnit',
  });

  const handleAddMaterial = React.useCallback(() => {
    const newLineItem: SalesOrderLineItemRecord = {
      id: `temp-${Date.now()}-${Math.random()}`,
      itemId: '',
      quantity: 0,
      unit: enumUnit.Pounds,
      unitPrice: zeroMoney(),
      totalPrice: zeroMoney(),
    };
    setDraftLineItems([...effectiveLineItems, newLineItem]);
  }, [effectiveLineItems, setDraftLineItems]);

  const handleUpdateLineItem = React.useCallback(
    (lineItemId: string, updates: Partial<LoadFormLineItem>) => {
      const index = effectiveLineItems.findIndex(
        (item) => item.id === lineItemId,
      );
      if (index === -1) return;

      const updatedItems = [...effectiveLineItems];
      const currentItem = updatedItems[index];
      const updatedItem: SalesOrderLineItemRecord = { ...currentItem };

      if (updates.itemId !== undefined) {
        updatedItem.itemId = updates.itemId;
      }

      if (
        updates.weights !== undefined &&
        updates.weights?.[enumLoadWeightType.Net] !== null
      ) {
        updatedItem.quantity = updates.weights?.[enumLoadWeightType.Net]?.value;
        updatedItem.unit = updates.weights?.[enumLoadWeightType.Net]?.unit;
      }

      if (updates.unitPrice !== undefined && updates.unitPrice !== null) {
        updatedItem.unitPrice = updates.unitPrice;
      }

      if (
        updates.weights?.[enumLoadWeightType.Net] !== undefined ||
        updates.unitPrice !== undefined
      ) {
        updatedItem.totalPrice = calculateTotalPrice(
          updatedItem.unitPrice,
          updatedItem.quantity,
        );
      }

      updatedItems[index] = updatedItem;

      setDraftLineItems(updatedItems);
    },
    [effectiveLineItems, setDraftLineItems],
  );

  const handleDeleteLineItem = React.useCallback(
    (lineItemId: string) => {
      const filteredItems = effectiveLineItems.filter(
        (item) => item.id !== lineItemId,
      );
      setDraftLineItems(filteredItems);
    },
    [effectiveLineItems, setDraftLineItems],
  );

  const handleDiscard = React.useCallback(() => {
    setDraftLineItems(null);
  }, [setDraftLineItems]);

  const handleSave = React.useCallback(async () => {
    if (!salesOrder?.id || isSaving) return;

    const lineItemErrors = validateLineItems(effectiveLineItems);

    if (Object.keys(lineItemErrors).length > 0) {
      setLineItemErrors(lineItemErrors);
      toast.error(t.formErrors.salesOrders.materials.errors);
      return;
    }

    setIsSaving(true);
    try {
      const validLineItems = effectiveLineItems.filter(
        (
          item,
        ): item is typeof item & {
          itemId: string;
          quantity: number;
          unit: string;
          unitPrice: NonNullable<typeof item.unitPrice>;
        } =>
          !!item.itemId &&
          item.itemId.trim() !== '' &&
          item.quantity !== null &&
          item.quantity > 0 &&
          !!item.unit &&
          !!item.unitPrice,
      );

      await updateSalesOrder({
        id: salesOrder.id,
        weightDisplayUnit: localWeightDisplayUnit,
        lineItems: validLineItems.map((item) => ({
          id: item.id.startsWith('temp-') ? undefined : item.id,
          itemId: item.itemId,
          quantity: item.quantity,
          unit: item.unit as Unit,
          unitPrice: toMoneyInput(item.unitPrice),
          totalPrice: toMoneyInput(item.totalPrice),
        })),
      });
      toast.success(t.salesOrderDetail.messages.materialsUpdated);
    } catch (error) {
      toast.error(
        t.salesOrderDetail.messages.materialsUpdateError(
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    salesOrder?.id,
    localWeightDisplayUnit,
    effectiveLineItems,
    isSaving,
    updateSalesOrder,
    toast,
    t,
  ]);

  // Effects
  /** Syncs local weight display unit with remote sales order */
  React.useEffect(() => {
    if (salesOrder?.weightDisplayUnit !== undefined) {
      setLocalWeightDisplayUnit(salesOrder.weightDisplayUnit ?? null);
    }
  }, [salesOrder?.weightDisplayUnit]);

  if (!salesOrder) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex-1 p-6">
          <div className="rounded-lg bg-white p-6">
            <div className="py-8 text-center">
              <p className="text-neutral/50">
                {t.salesOrderDetail.materials.noSalesOrderSelected}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SalesDetailLayout
      className="@SalesOrderTabMaterials"
      variant={variant}
      sidebar={
        showInventoryAvailability ? <InventoryAvailability /> : undefined
      }
      footer={
        !disabled &&
        (hasDraftChanges || hasWeightDisplayUnitChanges) && (
          <div className="flex flex-shrink-0 items-center gap-3">
            <Button
              color="neutral"
              variant="outline"
              onPress={handleDiscard}
              isDisabled={isSaving}
            >
              {t.salesOrderDetail.actions.discard}
            </Button>
            <Button
              color="primary"
              variant="solid"
              onPress={handleSave}
              isPending={isSaving}
              isLoading={isSaving}
              isDisabled={!hasDraftChanges && !hasWeightDisplayUnitChanges}
            >
              {isMissingRequired && isDraftStatus
                ? t.loadDetail.footer.saveDraft
                : t.salesOrderDetail.actions.saveUpdates}
            </Button>
          </div>
        )
      }
    >
      {loadFormLineItems.length === 0 ? (
        <div className="py-12 text-center">
          <div className="mb-6 text-neutral/50">
            <p className="mb-2 text-lg">
              {t.salesOrderDetail.materials.emptyTitle}
            </p>
            <p className="text-sm">
              {t.salesOrderDetail.materials.emptyDescription}
            </p>
          </div>
          {!disabled && (
            <Button
              variant="outline"
              color="primary"
              onPress={handleAddMaterial}
              size="large"
            >
              {t.salesOrderDetail.actions.addMaterial}
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="mb-4 flex justify-between">
            <h3 className="text-lg font-semibold">
              {t.itemTable.salesOrderTitle}
            </h3>
            <div className="flex justify-end gap-2">
              <WeightUnitSwitch
                value={currentWeightUnit}
                onChange={handleWeightUnitChange}
              />
              <InventoryAvailabilitySwitch />
            </div>
          </div>
          <SalesOrderMaterialsTable
            lineItems={loadFormLineItems}
            onUpdateLineItem={handleUpdateLineItem}
            onDeleteLineItem={handleDeleteLineItem}
            onAddLineItem={handleAddMaterial}
            disabled={disabled || isSaving}
            weightUnit={currentWeightUnit}
            errors={materialsErrors}
          />
        </>
      )}
    </SalesDetailLayout>
  );
};
